'use client';

import { CircularProgress } from '@mui/material';
import { asLinkAttrs } from '@prismicio/client';
import { PrismicNextLinkProps } from '@prismicio/next';
import clsx from 'clsx';
import Link, { LinkProps } from 'next/link';

import { ButtonHTMLAttributes, useState } from 'react';

import * as styles from './call-to-action.css';

import { IconArrowRight } from './icons/arrow-right';

type BaseCalloutProps = {
  hasArrow?: boolean;
};

type ButtonCallToActionProps = {
  type: 'button' | 'submit';
  label: string;
  className?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>;

type NextCallToActionProps = {
  label: string;
  type: 'next';
  className?: string;
} & Omit<LinkProps, 'children'>;

type PrismicCallToActionProps = {
  type: 'prismic';
} & PrismicNextLinkProps;

export type CallToActionProps = BaseCalloutProps &
  (NextCallToActionProps | PrismicCallToActionProps | ButtonCallToActionProps);

export function CallToAction(props: CallToActionProps) {
  const [spin, setSpin] = useState<boolean>(false);

  if (props.type === 'next') {
    const { label, className, hasArrow, ...nextProps } = props;
    return (
      <Link
        {...nextProps}
        className={clsx(styles.wrapper, className)}
        onClick={() => {
          setSpin(true);
        }}
      >
        {spin ? (
          <span className={styles.label}>
            <CircularProgress
              size={`${13 / 16}rem`}
              sx={{ color: '#EAF2FF' }}
            />
          </span>
        ) : (
          <span className={styles.label}>{label}</span>
        )}
        {hasArrow !== false && <IconArrowRight className={styles.icon} />}
      </Link>
    );
  } else if (props.type === 'button' || props.type === 'submit') {
    const { className, hasArrow, label, ...buttonProps } = props;
    return (
      <button
        {...buttonProps}
        className={clsx(styles.wrapper, className)}
        onClick={(event) => {
          setSpin(true);
          // `onClick` is optional on ButtonHTMLAttributes; in stricter TS
          // types from React 19.2 / @types/react 19.2 we have to guard
          // and forward the event explicitly.
          buttonProps.onClick?.(event);
        }}
      >
        {spin ? (
          <span className={styles.label}>
            <CircularProgress
              size={`${13 / 16}rem`}
              sx={{ color: '#EAF2FF' }}
            />
          </span>
        ) : (
          <span className={styles.label}>{label}</span>
        )}
        {hasArrow !== false && <IconArrowRight className={styles.icon} />}
      </button>
    );
  } else if (props.type === 'prismic') {
    const {
      field,
      document,
      linkResolver,
      children,
      className,
      hasArrow,
      ...restProps
    } = props;

    const {
      href: computedHref,
      rel: computedRel,
      ...attrs
    } = asLinkAttrs(field ?? document, {
      linkResolver,
      rel: typeof restProps.rel === 'function' ? restProps.rel : undefined,
    });

    const href = ('href' in restProps ? restProps.href : computedHref) ?? '';

    let rel = computedRel;
    if ('rel' in restProps && typeof restProps.rel !== 'function') {
      rel = restProps.rel;
    }

    return (
      <Link
        {...attrs}
        {...restProps}
        href={href}
        rel={rel}
        className={clsx(styles.wrapper, className)}
      >
        <span className={styles.label}>
          {'children' in props ? children : field?.text}
        </span>
        {hasArrow !== false && <IconArrowRight className={styles.icon} />}
      </Link>
    );
  }
}
